import type { DailyMenu, Feedback, FoodWaste, MealOrder, MealPlan, MenuItem, StudentMealPlan } from "../../services/cafeteriaService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The cafeteria: menus planned by the kitchen, orders families (or the
 * counter) place against them, meal plans that make orders free, and the
 * kitchen's feedback and waste records. What can be done with a record here
 * mirrors the server (apps/cafeteria/views.py advance_order and friends).
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const time = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const ugx = (value?: string | number | null) => `UGX ${Number(value || 0).toLocaleString()}`;
/** "Tue 30 Sep" for a yyyy-mm-dd, read as a calendar day (no timezone shift). */
export const weekday = (value?: string | null) => {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

type Pairs = [string, string][];
export const PLAN_TYPES: Pairs = [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["semester", "Term"]];
export const WASTE_TYPES: Pairs = [["preparation", "Preparation"], ["overproduction", "Cooked too much"], ["spoilage", "Spoiled in store"], ["plate_waste", "Left on plates"], ["other", "Other"]];
export const UNITS = ["kg", "litres", "portions"];
export const RATINGS: Pairs = [["5", "Excellent"], ["4", "Good"], ["3", "Average"], ["2", "Poor"], ["1", "Very poor"]];
export const labelOf = (pairs: Pairs, value?: string | number | null) => pairs.find(([key]) => key === String(value))?.[1] ?? (value ? String(value) : "—");
export const stars = (rating?: number | null) => (rating ? `${"★".repeat(Math.round(rating))}${"☆".repeat(5 - Math.round(rating))}` : "—");

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

// ---- Menus ----------------------------------------------------------------------

export const placesLeft = (menu: Pick<DailyMenu, "planned_servings" | "ordered_count">) => Math.max(0, menu.planned_servings - menu.ordered_count);

export function menuStatus(menu: Pick<DailyMenu, "date" | "is_published" | "is_available" | "planned_servings" | "ordered_count">, today: string): Tone {
  if (!menu.is_published) return menu.date < today ? tone("Never published", "bg-gray-300") : tone("Draft", "bg-gray-400");
  if (menu.date < today) return tone("Past", "bg-gray-300");
  if (!menu.is_available) return tone("Ordering closed", "bg-amber-500");
  if (placesLeft(menu) === 0) return tone("Full", "bg-amber-500");
  return menu.date === today ? tone("Today", "bg-sky-500") : tone("Open for orders", "bg-emerald-500");
}

/** Whether a family can still order this meal: published, open, today or later, with places. */
export const menuOrderable = (menu: DailyMenu, today: string) =>
  menu.is_published && menu.is_available && menu.date >= today && placesLeft(menu) > 0 && menu.items.some((item) => item.is_available);

/** What the kitchen can do with a menu. A menu with orders can only be closed, reopened or have notes. */
export function menuActions(menu: DailyMenu, today: string) {
  const upcoming = menu.date >= today;
  const untouched = menu.ordered_count === 0;
  return {
    edit: upcoming && untouched,
    publish: upcoming && !menu.is_published && menu.items.length > 0,
    unpublish: upcoming && menu.is_published && untouched,
    close: upcoming && menu.is_published && menu.is_available,
    reopen: upcoming && menu.is_published && !menu.is_available,
    service: menu.is_published && menu.ordered_count > 0,
  };
}

export const allergensOf = (menu: Pick<DailyMenu, "items">) => [...new Set(menu.items.flatMap((item) => item.allergens ?? []))];
export const dishNames = (menu: Pick<DailyMenu, "items">) => menu.items.map((item) => item.name).join(", ");

// ---- Orders -----------------------------------------------------------------------

/** One step on through the kitchen, as the server allows it; null when nothing can move it. */
export function nextStep(order: Pick<MealOrder, "status" | "payment_status">): { status: MealOrder["status"]; label: string } | null {
  if (order.status === "pending") return order.payment_status === "paid" || order.payment_status === "free" ? { status: "confirmed", label: "Confirm" } : null;
  return ({ confirmed: { status: "preparing", label: "Start preparing" }, preparing: { status: "ready", label: "Mark ready" }, ready: { status: "served", label: "Served" } } as Record<string, { status: MealOrder["status"]; label: string }>)[order.status] ?? null;
}

export function orderStatus(order: Pick<MealOrder, "status" | "payment_status">): Tone {
  if (order.status === "pending") return order.payment_status === "pending" ? tone("Awaiting payment", "bg-amber-500") : tone("To confirm", "bg-sky-500");
  return ({ confirmed: tone("Confirmed", "bg-indigo-500"), preparing: tone("Preparing", "bg-violet-500"), ready: tone("Ready to serve", "bg-emerald-400"), served: tone("Served", "bg-emerald-600"), cancelled: tone("Cancelled", "bg-gray-400") } as Record<string, Tone>)[order.status];
}

export function paymentLabel(order: Pick<MealOrder, "payment_status" | "plan_name" | "amount">) {
  if (order.payment_status === "free") return order.plan_name ? `On meal plan (${order.plan_name})` : Number(order.amount) ? "Free" : "No charge";
  if (order.payment_status === "paid") return "Paid";
  if (order.payment_status === "refunded") return "Refunded";
  return "Not paid";
}

/** An order left behind: its meal day has passed but it was never served or cancelled. */
export const leftOpen = (order: Pick<MealOrder, "status" | "menu_date">, today: string) =>
  order.status !== "served" && order.status !== "cancelled" && Boolean(order.menu_date) && order.menu_date! < today;

/** What a person can do with an order now. `kitchen` is the cafeteria manager's view. */
export function orderActions(order: MealOrder, kitchen: boolean) {
  const unpaid = order.payment_status !== "paid";
  return {
    next: kitchen ? nextStep(order) : null,
    // The kitchen can cancel until cooking starts; a family only until the kitchen confirms.
    cancel: kitchen ? (order.status === "pending" || order.status === "confirmed") && unpaid : order.status === "pending" && unpaid,
    needsRefund: order.payment_status === "paid" && (order.status === "pending" || order.status === "confirmed"),
  };
}

/** For the service sheet: the steps present among the chosen orders, with the orders each moves. */
export function bulkSteps(orders: MealOrder[]) {
  const steps = new Map<string, { status: MealOrder["status"]; label: string; ids: number[] }>();
  for (const order of orders) {
    const next = nextStep(order);
    if (!next) continue;
    if (!steps.has(next.status)) steps.set(next.status, { ...next, ids: [] });
    steps.get(next.status)!.ids.push(order.id);
  }
  const order = ["confirmed", "preparing", "ready", "served"];
  return [...steps.values()].sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
}

export const SHEET_STAGES: { key: string; label: string; match?: (order: MealOrder) => boolean }[] = [
  { key: "open", label: "Still to serve", match: (order) => order.status !== "served" && order.status !== "cancelled" },
  { key: "unpaid", label: "Awaiting payment", match: (order) => order.status === "pending" && order.payment_status === "pending" },
  { key: "confirm", label: "To confirm", match: (order) => order.status === "pending" && order.payment_status !== "pending" },
  { key: "kitchen", label: "Confirmed or preparing", match: (order) => order.status === "confirmed" || order.status === "preparing" },
  { key: "ready", label: "Ready", match: (order) => order.status === "ready" },
  { key: "served", label: "Served", match: (order) => order.status === "served" },
  { key: "all", label: "All" },
];

// ---- Meal plans ------------------------------------------------------------------

export const planMeals = (plan: Pick<MealPlan, "breakfast_included" | "lunch_included" | "dinner_included" | "snacks_included">) =>
  ([["Breakfast", plan.breakfast_included], ["Lunch", plan.lunch_included], ["Dinner", plan.dinner_included], ["Snacks", plan.snacks_included]] as [string, boolean][])
    .filter(([, included]) => included).map(([label]) => label);

export const planOpen = (plan: Pick<MealPlan, "is_active" | "valid_to">, today: string) => plan.is_active && plan.valid_to >= today;

export function planStatus(plan: Pick<MealPlan, "is_active" | "valid_from" | "valid_to">, today: string): Tone {
  if (!plan.is_active) return tone("Withdrawn", "bg-gray-400");
  if (plan.valid_to < today) return tone("Ended", "bg-gray-300");
  if (plan.valid_from > today) return tone("Starts later", "bg-sky-500");
  return tone("Open", "bg-emerald-500");
}

export function subscriptionStatus(row: Pick<StudentMealPlan, "status" | "end_date" | "meals_remaining">, today: string): Tone {
  if (row.status === "pending") return tone("Awaiting payment", "bg-amber-500");
  if (row.status === "cancelled") return tone("Cancelled", "bg-gray-400");
  if (row.end_date < today) return tone("Ended", "bg-gray-300");
  if (row.meals_remaining === 0) return tone("All meals used", "bg-gray-400");
  return tone("Active", "bg-emerald-500");
}

/** The server cancels only an open subscription that nobody has paid for and no meal has been taken on. */
export const subscriptionCancellable = (row: Pick<StudentMealPlan, "status" | "financial_payment" | "meals_used">) =>
  (row.status === "pending" || row.status === "active") && !row.financial_payment && row.meals_used === 0;

// ---- Feedback ----------------------------------------------------------------------

/** Served meals a family has not rated yet, newest first. */
export function feedbackDue(orders: MealOrder[], feedback: Pick<Feedback, "student" | "daily_menu">[]) {
  const rated = new Set(feedback.map((row) => `${row.student}:${row.daily_menu}`));
  return orders
    .filter((order) => order.status === "served" && !rated.has(`${order.student}:${order.daily_menu}`))
    .sort((a, b) => (b.menu_date ?? "").localeCompare(a.menu_date ?? ""));
}

export function feedbackSummary(rows: Feedback[]) {
  const mean = (pick: (row: Feedback) => number) => (rows.length ? rows.reduce((sum, row) => sum + pick(row), 0) / rows.length : 0);
  return {
    count: rows.length,
    overall: mean((row) => row.overall_rating),
    taste: mean((row) => row.taste_rating),
    quality: mean((row) => row.quality_rating),
    service: mean((row) => row.service_rating),
    recommend: rows.length ? Math.round((rows.filter((row) => row.would_recommend).length / rows.length) * 100) : 0,
  };
}

// ---- Waste -----------------------------------------------------------------------

export function wasteTotals(rows: FoodWaste[], month: string) {
  const inMonth = rows.filter((row) => row.date.startsWith(month));
  const byType = new Map<string, number>();
  for (const row of inMonth) byType.set(row.waste_type, (byType.get(row.waste_type) ?? 0) + Number(row.cost_impact || 0));
  return {
    records: inMonth.length,
    cost: inMonth.reduce((sum, row) => sum + Number(row.cost_impact || 0), 0),
    kg: inMonth.filter((row) => row.unit === "kg").reduce((sum, row) => sum + Number(row.quantity), 0),
    worst: [...byType.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "",
  };
}

// ---- Registers ---------------------------------------------------------------------

const learnerSearch = (row: { student_name: string; student_number?: string; class_name?: string | null }) => `${row.student_name} ${row.student_number ?? ""} ${row.class_name ?? ""}`;

export function menuConfig(today: string, kitchen: boolean): RegisterConfig<DailyMenu> {
  return {
    search: (row) => `${row.meal_category_name} ${dishNames(row)} ${row.notes ?? ""}`,
    views: kitchen
      ? [
          { key: "upcoming", label: "Today and coming", match: (row) => row.date >= today },
          { key: "today", label: "Today", match: (row) => row.date === today },
          { key: "drafts", label: "Drafts", match: (row) => !row.is_published && row.date >= today },
          { key: "past", label: "Past", match: (row) => row.date < today },
          { key: "all", label: "All" },
        ]
      : [
          { key: "open", label: "Open for orders", match: (row) => menuOrderable(row, today) },
          { key: "all", label: "All coming up" },
        ],
    date: { label: "Day", value: (row) => row.date },
    category: { label: "Meal", value: (row) => row.meal_category_name },
    sorts: [
      { key: "soonest", label: "Soonest first", compare: byText((row) => `${row.date}${row.serving_time_start ?? ""}`) },
      { key: "latest", label: "Latest first", compare: reversed(byText((row) => `${row.date}${row.serving_time_start ?? ""}`)) },
    ],
  };
}

export function orderConfig(today: string, kitchen: boolean): RegisterConfig<MealOrder> {
  const open = (row: MealOrder) => row.status !== "served" && row.status !== "cancelled";
  return {
    search: (row) => `${learnerSearch(row)} ${row.daily_menu_name} ${row.receipt_number ?? ""}`,
    views: kitchen
      ? [
          { key: "today", label: "Today", match: (row) => row.menu_date === today && row.status !== "cancelled" },
          { key: "unpaid", label: "Awaiting payment", match: (row) => row.status === "pending" && row.payment_status === "pending" },
          { key: "confirm", label: "To confirm", match: (row) => row.status === "pending" && row.payment_status !== "pending" },
          { key: "kitchen", label: "In the kitchen", match: (row) => row.status === "confirmed" || row.status === "preparing" || row.status === "ready" },
          { key: "served", label: "Served", match: (row) => row.status === "served" },
          { key: "left_open", label: "Left open", match: (row) => leftOpen(row, today) },
          { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
          { key: "all", label: "All" },
        ]
      : [
          { key: "coming", label: "Coming up", match: (row) => open(row) && (row.menu_date ?? "") >= today },
          { key: "unpaid", label: "To pay", match: (row) => row.status === "pending" && row.payment_status === "pending" },
          { key: "served", label: "Served", match: (row) => row.status === "served" },
          { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
          { key: "all", label: "All" },
        ],
    category: { label: "Meal", value: (row) => row.daily_menu_name },
    sorts: [
      { key: "meal", label: "By meal day", compare: byText((row) => `${row.menu_date}${row.daily_menu_name}${row.class_name ?? ""}${row.student_name}`) },
      { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
      { key: "class", label: "By class", compare: byText((row) => `${row.class_name ?? ""}${row.student_name}`) },
      { key: "newest", label: "Newest orders first", compare: reversed(byText((row) => `${row.order_date}${row.order_time ?? ""}`)) },
    ],
  };
}

export const itemConfig: RegisterConfig<MenuItem> = {
  search: (row) => `${row.name} ${row.category_name} ${row.description ?? ""} ${(row.allergens ?? []).join(" ")}`,
  views: [
    { key: "serving", label: "Being served", match: (row) => row.is_available },
    { key: "vegetarian", label: "Vegetarian", match: (row) => row.is_available && row.is_vegetarian },
    { key: "allergens", label: "With allergens", match: (row) => row.is_available && (row.allergens ?? []).length > 0 },
    { key: "retired", label: "Retired", match: (row) => !row.is_available },
    { key: "all", label: "All" },
  ],
  category: { label: "Meal", value: (row) => row.category_name },
  sorts: [
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
    { key: "price", label: "Dearest first", compare: reversed(byNumber((row) => row.price)) },
    { key: "used", label: "Most served", compare: reversed(byNumber((row) => row.menu_count ?? 0)) },
  ],
};

export function planConfig(today: string, kitchen: boolean): RegisterConfig<MealPlan> {
  return {
    search: (row) => `${row.name} ${row.description ?? ""} ${planMeals(row).join(" ")}`,
    views: kitchen
      ? [
          { key: "open", label: "Open", match: (row) => planOpen(row, today) },
          { key: "requests", label: "With requests", match: (row) => (row.pending_count ?? 0) > 0 },
          { key: "ended", label: "Ended or withdrawn", match: (row) => !planOpen(row, today) },
          { key: "all", label: "All" },
        ]
      : [{ key: "all", label: "All" }],
    category: { label: "Kind", value: (row) => labelOf(PLAN_TYPES, row.plan_type) },
    sorts: [
      { key: "popular", label: "Popular first", compare: (a, b) => Number(Boolean(b.is_popular)) - Number(Boolean(a.is_popular)) || a.name.localeCompare(b.name) },
      { key: "price", label: "Cheapest first", compare: byNumber((row) => row.price) },
      { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
    ],
  };
}

export function subscriptionConfig(today: string): RegisterConfig<StudentMealPlan> {
  return {
    search: (row) => `${learnerSearch(row)} ${row.meal_plan_name} ${row.receipt_number ?? ""}`,
    views: [
      { key: "active", label: "Active", match: (row) => subscriptionStatus(row, today).label === "Active" },
      { key: "pending", label: "Awaiting payment", match: (row) => row.status === "pending" },
      { key: "ended", label: "Ended", match: (row) => row.status === "active" && subscriptionStatus(row, today).label !== "Active" },
      { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
      { key: "all", label: "All" },
    ],
    date: { label: "Starts", value: (row) => row.start_date },
    category: { label: "Plan", value: (row) => row.meal_plan_name },
    sorts: [
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => `${row.start_date}${row.created_at ?? ""}`)) },
      { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
      { key: "ending", label: "Ending soonest", compare: byText((row) => row.end_date) },
    ],
  };
}

export function feedbackConfig(kitchen: boolean): RegisterConfig<Feedback> {
  return {
    search: (row) => `${learnerSearch(row)} ${row.daily_menu_name} ${row.comments ?? ""} ${row.suggestions ?? ""}`,
    views: kitchen
      ? [
          { key: "all", label: "All" },
          { key: "low", label: "Poor or very poor", match: (row) => row.overall_rating <= 2 },
          { key: "comments", label: "With comments", match: (row) => Boolean(row.comments?.trim() || row.suggestions?.trim()) },
          { key: "no_again", label: "Would not order again", match: (row) => !row.would_order_again },
        ]
      : [{ key: "all", label: "All" }],
    date: { label: "Meal day", value: (row) => row.menu_date },
    category: { label: "Meal", value: (row) => row.daily_menu_name },
    sorts: [
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.created_at)) },
      { key: "lowest", label: "Lowest rated first", compare: byNumber((row) => row.overall_rating) },
    ],
  };
}

export const wasteConfig: RegisterConfig<FoodWaste> = {
  search: (row) => `${labelOf(WASTE_TYPES, row.waste_type)} ${row.menu_item_name ?? ""} ${row.reason ?? ""} ${row.action_taken ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "costly", label: "With a cost", match: (row) => Number(row.cost_impact || 0) > 0 },
    { key: "no_action", label: "No action written", match: (row) => !row.action_taken?.trim() },
  ],
  date: { label: "Date", value: (row) => row.date },
  category: { label: "Kind", value: (row) => labelOf(WASTE_TYPES, row.waste_type) },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => `${row.date}${row.created_at ?? ""}`)) },
    { key: "cost", label: "Costliest first", compare: reversed(byNumber((row) => row.cost_impact ?? 0)) },
  ],
};
